import { liquidSettleEvent } from '@/play/liquid/changes'
import { liquidKindByColor } from '@/play/liquid/kindByColor'
import { BLOB_MOLTEN_SILVER_COLOR } from '@/voxels/blob_sdf/constants'
import type { VoxelMaterial } from '@/voxelTypes'

/**
 * The potions, by colour: five bright goos (the paint study's `shiny`, as 031 and 032) and two
 * liquid metals (007's `metallic` silver and gold). The goos are one material, so where they
 * touch they are one surface (`hints.blobSeparateColors: false`) coloured cell by cell with a soft
 * border: a band one cell thick still shows as a stripe of its colour. 017 gave each liquid its own
 * material because a fused pool was then drawn in one average colour (mud); the surface now
 * colours by the nearest cells, so potions keep their colours. Each colour its own surface (the
 * settings switch) melts one-cell bands and streams away. The metals are their own surface (another
 * material). The sim knows a cell only by its colour, so the colours are all distinct.
 */
export const POTION_PINK = 0xff3fb4
export const POTION_LIME = 0x6dff3a
export const POTION_CYAN = 0x2ef2ff
export const POTION_ORANGE = 0xff8a2a
export const POTION_VIOLET = 0xa45bff
export const POTION_SILVER = BLOB_MOLTEN_SILVER_COLOR
export const POTION_GOLD = 0xf0cf86

/** The potion pots, left to right in the top bar (goos, then the metals). */
export const POTION_COLORS = [POTION_PINK, POTION_LIME, POTION_CYAN, POTION_ORANGE, POTION_VIOLET, POTION_SILVER, POTION_GOLD] as const

const METALS = new Set<number>([POTION_SILVER, POTION_GOLD])
export const isPotionMetal = (color: number) => METALS.has(color >>> 0)

/**
 * How heavy each potion is: heavier sinks through lighter, lighter rises. Metal is about three
 * times any goo (gold the heaviest), so it always ends at the bottom. The goos differ a little, so
 * where they meet they settle into bands (violet, cyan, lime, orange, pink from the bottom) instead
 * of interleaving cell by cell, which equal densities do where two streams cross (017).
 */
const DENSITY = new Map<number, number>([
  [POTION_GOLD, 3.4],
  [POTION_SILVER, 3],
  [POTION_VIOLET, 1.25],
  [POTION_CYAN, 1.2],
  [POTION_LIME, 1.15],
  [POTION_ORANGE, 1.1],
  [POTION_PINK, 1],
])
export const potionDensityOf = (color: number) => DENSITY.get(color >>> 0) ?? 1
export const potionMaterialOf = (color: number): VoxelMaterial => (isPotionMetal(color) ? 'metallic' : 'shiny')

/** Every blob in the lab flows, in one sim (so they displace each other); a moved cell keeps its material. */
export const POTION_LIQUID = liquidKindByColor('blob', potionMaterialOf, { defaultColor: POTION_PINK })

/**
 * One frame of the flow, or one fill or pour: the change set of what moved or was poured in. A
 * press (fill, pour) and the flow it starts are one undo step; a hole and everything that ran out
 * of it are one.
 */
export const OOZE_2_FLOW = liquidSettleEvent('experience.ooze-2.flow', POTION_LIQUID)

/** Reducer handlers for Ooze 2 (`experience.ooze-2.*`). */
export const eventHandlers = OOZE_2_FLOW.handlers
